/**
 * Hoshin Kanri (policy-deployment) domain model — the FE↔BE contract for the
 * Hoshin module. Ported from the pbdesign behaviour oracle
 * (`src/wireframe/data/hoshin.ts` + `…/session/catchball.ts`). Seed fixtures live
 * in the mock layer (`@playbookai/hoshin-feature/src/api/mock`), NOT here.
 *
 * - Owners / reviewers are the shared `UserRef {id,name}` (sourced from
 *   Auth/RBAC); the design system derives initials from the name. The oracle's
 *   mock `role`/`initials` are intentionally dropped.
 * - Share access speaks the shared `ModuleRole` vocabulary (`./module-base.ts`)
 *   on both axes — named members and the share link. `owner` is never a
 *   grantable value: Hoshin ownership is the `plans.owner_user_id` column.
 * - Status→tone/colour and label maps are PRESENTATION concerns owned by the
 *   design system — by design they are not modelled here (this package stays
 *   runtime-dep-free, ADR-0012).
 */
// --- Enumerations -----------------------------------------------------------
/** Red / Amber / Green health status. `unknown` = not yet assessed. */
export const RAG_STATUSES = ["green", "amber", "red", "unknown"];
/**
 * Organisation level a plan sits at — a display / grouping label. The oracle has
 * no cross-level hierarchy (plans are independent); cascade is intra-plan via
 * `XMatrixItem.linkedItemIds`.
 */
export const ORG_LEVELS = [
    "company",
    "region",
    "plant",
    "department",
    "team",
];
/**
 * Lifecycle status of a Hoshin plan — surfaced as the workspace status pill
 * (PB-222). Distinct from `health.overallStatus` (RAG): this tracks where the
 * plan sits in its planning cycle, not how it is performing.
 */
export const HOSHIN_PLAN_STATUSES = [
    "draft",
    "active",
    "review",
    "closed",
    "archived",
];
/** The four X-Matrix quadrants (rotation ①②③④). */
export const XMATRIX_ITEM_TYPES = [
    "breakthrough3Year",
    "annualObjective",
    "target",
    "improvementPriority",
];
/**
 * Reader-facing names for the four X-Matrix quadrants.
 *
 * These live HERE, not in `packages/hoshin-feature`, because the audit phrase
 * layer in `@playbookai/api-clients` needs them and a non-feature package may
 * not import a `*-feature` sibling (ADR-0009). Same reasoning that puts
 * `VSM_ARTIFACT_LABELS` and `VSM_SHAPE_CATALOGUE` in this package.
 *
 * Every value is AUTHORED IN CODE — none of it is user text — so it is safe to
 * resolve into an audit row's copy. An item's own title is user data and never
 * reaches the phrase; only its quadrant does.
 *
 * WORDED AS THE ROOM SAYS THEM, not as the enum spells them: the rail reads
 * "added a 3-year breakthrough", never "added a breakthrough3Year". "Target to
 * improve" is the full Hoshin name for the `target` quadrant, which is why it
 * is not just "Target".
 */
export const XMATRIX_ITEM_LABELS = {
    breakthrough3Year: "3-year breakthrough",
    annualObjective: "annual objective",
    target: "target to improve",
    improvementPriority: "improvement priority",
};
// --- Workshop session (PB-264 / HWS-01) -------------------------------------
/**
 * The canonical 6-step Hoshin workshop sequence — forward-only, terminal =
 * `xmatrix`. Single-sourced here so the backend (step machine, DTO validation,
 * OpenAPI enum, CHECK-constraint drift test) and the FE step machine all agree.
 * FE-name mapping: brainstorm≈ideate, group≈categorize.
 *
 * The workshop ENDS at the X-Matrix: building the matrix is the session's
 * deliverable. Tracking against it (the bowling chart) is ongoing plan work, not
 * a workshop step — it lives in the Hoshin workspace Monitor mode and the
 * `/v1/hoshin-kpis` read model, both untouched by this vocabulary.
 */
export const HOSHIN_STEPS = [
    // PB-5156 — the CASCADE step, and the only one that is not universal: a child
    // plan opens on its parent's X-Matrix and the room votes which of the
    // parent's improvement priorities come down. A plan with no parent has
    // nothing to vote on and never enters it (see `stepsForPlan`).
    //
    // FIRST on purpose: what the room inherits is the context everything after it
    // is ideated against, so it cannot come later.
    "cascade",
    "ideate",
    "present",
    "categorize",
    "vote",
    "prioritize",
    "xmatrix",
];
/**
 * The BOARDS a workshop idea can be captured or parked on, earliest first.
 *
 * A step is not fine enough to say "which screen". Ideate shows TWO boards —
 * the private "Submit ideas" drafts board, and the collective board the room
 * lands on once someone submits — and the workshop step reads `ideate` on both,
 * because submitting opens a view and does not advance the step. A rule keyed
 * on the step therefore cannot tell them apart, and an idea added on the
 * collective board turned up on the private one the moment anyone stepped back
 * (PB-5329).
 *
 * Every other step has exactly one board and keeps its own name.
 */
export const HOSHIN_IDEA_BOARDS = [
    "cascade",
    "ideate_private",
    "ideate_collective",
    "present",
    "categorize",
    "vote",
    "prioritize",
    "xmatrix",
];
/** The boards a step can be showing, earliest first. */
export function boardsForStep(step) {
    return step === "ideate" ? ["ideate_private", "ideate_collective"] : [step];
}
/**
 * The board to assume when a caller names none — the step's EARLIEST.
 *
 * Fail-open, like the two rules below: an idea whose board was never recorded
 * shows from the first board of its step onward rather than hiding behind a
 * later one.
 */
export function defaultBoardForStep(step) {
    const [first] = boardsForStep(step);
    return first ?? "ideate_private";
}
/**
 * Was this idea already captured by the time the room reached `board`?
 *
 * `null` predates board stamping and is treated as "always was", so no existing
 * idea disappears. An unknown board name fails the same way, open rather than
 * hiding something real.
 */
export function ideaExistedOnBoard(createdOnBoard, board) {
    if (createdOnBoard === null)
        return true;
    const made = HOSHIN_IDEA_BOARDS.indexOf(createdOnBoard);
    const now = HOSHIN_IDEA_BOARDS.indexOf(board);
    if (made < 0 || now < 0)
        return true;
    return made <= now;
}
/**
 * Is this idea set aside on some OTHER board than `board`?
 *
 * Parked with no board recorded predates the stamp and stays visible, the same
 * fail-open as {@link ideaExistedOnBoard}: a rule about bookkeeping must not
 * make a real idea disappear.
 */
export function parkedOnAnotherBoard(idea, board) {
    if (!idea.parked)
        return false;
    if (idea.parkedOnBoard === null)
        return false;
    return idea.parkedOnBoard !== board;
}
/**
 * Reader-facing names for the workshop steps.
 *
 * Here rather than in `packages/hoshin-feature` for the same reason as
 * `XMATRIX_ITEM_LABELS`: the audit phrase layer in `@playbookai/api-clients`
 * needs them and may not import a `*-feature` sibling (ADR-0009).
 *
 * A history line that says only "moved the workshop on a step" leaves the
 * reader to count: the whole point of the row is WHICH step the room is on now.
 * Every value is authored in code, so it is safe to resolve into audit copy.
 */
export const HOSHIN_STEP_LABELS = {
    cascade: "Cascade",
    ideate: "Ideate",
    present: "Present",
    categorize: "Group",
    vote: "Vote",
    prioritize: "Prioritize",
    xmatrix: "X-Matrix",
};
/**
 * Workshop lifecycle status — distinct from the working step. Steps only cycle
 * after Start; `completed` is a label and keeps the same workflow as `running`.
 */
export const HOSHIN_WORKSHOP_STATUSES = [
    "draft",
    "running",
    "completed",
    "archived",
];
/** Completion labels a started workshop; only draft/archived block live work. */
export function isActiveHoshinWorkshopStatus(status) {
    return status === "running" || status === "completed";
}
/**
 * Workshop participant roster role — the workshop RBAC axis, distinct from the
 * plan role axis (`HoshinRole`: owner/collaborator/viewer). Facilitator drives
 * the session; participants/observers read.
 */
export const HOSHIN_WORKSHOP_ROLES = [
    "facilitator",
    "participant",
    "observer",
];
// --- KPIs (bowling chart) ---------------------------------------------------
/**
 * Data-availability of a governed KPI's source, mirroring the platform
 * `kpi.kpi_current_states.measurement_status` (PB-92). Mock-supplied today; the
 * live path binds it from the HTTI-03 bowling read model (PB-282). `not_connected`
 * = no approved data product yet ("Data Product Required"); `current` = fresh
 * measured data; `stale`/`unavailable` = source degraded.
 */
export const MEASUREMENT_STATUSES = [
    "not_connected",
    "current",
    "stale",
    "unavailable",
];
/**
 * A Hoshin TTI's derived state (PB-237). Computed by `deriveTtiState(item, kpi?)`
 * from the `target` X-matrix item's KPI link + target plus the linked KPI's
 * `measurementStatus` — never stored. `missing` surfaces as "Data Product
 * Required"; `inactive` is a draft target with no value yet.
 */
export const TTI_STATES = ["active", "inactive", "stale", "missing"];
/**
 * Why a TTI is not measurable (PB-283). `TtiState` alone could not distinguish
 * the two situations that both read `missing` — no KPI chosen yet, versus a KPI
 * chosen but with no approved data product. They need different user actions
 * ("pick a measure" / "wait for data"), and conflating them made the UI tell a
 * user with no KPI to wait for data that was never coming.
 *
 * A reason exists ONLY when the TTI is not measurable, i.e. when
 * {@link classifyTtiState} reads `missing` or `inactive`. `stale` and
 * `unavailable` deliberately have no reason: `measurementStatus` already states
 * those facts precisely, and a second field restating them would be a drift
 * risk with no new information.
 *
 * `data_product_required` is broader than its name: under ADR-0039 a KPI that is
 * not lifecycle-active contributes no measurement signal, so an **archived** KPI
 * folds here too — and that KPI may have a perfectly good data product. Treat the
 * value as "no governed data is available for the linked measure", and do not
 * render copy asserting the data product specifically. Splitting the two apart
 * needs an additional reason (`kpi_not_active`), which is ADR-0039's own deferred
 * fix and is NOT what `awaiting_manual_update` below is for.
 *
 * `awaiting_manual_update` (PB-2924) is the manual-tracking counterpart: the TTI
 * is tracked by hand and its owner has not recorded an actual yet. It replaces
 * `data_product_required` — and `kpi_not_linked` — whenever
 * `trackingMode === "manual"`, because neither instruction is true there. No data
 * product is coming, and a manually tracked TTI needs no KPI at all.
 */
export const TTI_STATE_REASONS = [
    "kpi_not_linked",
    "data_product_required",
    "target_value_not_set",
    "awaiting_manual_update",
];
/**
 * How a TTI's actual value arrives (PB-2924). Stored as
 * `hoshin.targets.tracking_mode`.
 *
 * `manual` — an owner records the month's actual by hand on the bowling chart.
 * The MVP default, because the data plane behind `data_product` is deferred
 * (canonical §9), so in practice every TTI is manual today.
 *
 * `data_product` — measurement is expected from a governed KPI's data binding.
 * Only in this mode does an absent measurement mean "Data Product Required"; in
 * `manual` mode it means "awaiting the owner's update", which is a different
 * instruction to a different person.
 *
 * Deliberately a property of the TTI, not of the linked KPI: a manually tracked
 * TTI may link no KPI at all, and §17's rejection of manual entry on a governed
 * KPI still stands — ADR-0028 keeps the two targets separate.
 */
export const TTI_TRACKING_MODES = ["manual", "data_product"];
/**
 * Pure TTI-state truth table (PB-237, first-match-wins) — the single cross-layer
 * source of truth for `TtiState`, shared by the platform backend (PB-96) and the
 * FE `deriveTtiState`. `missing` = not measurable; never stored, always derived.
 *
 * `trackingMode` is deliberately NOT read here. This ticket's first implementation
 * let it select an entire state table, which regressed every KPI-measured TTI to
 * `missing`: the column defaults to `manual`, nothing in the product can set it to
 * `data_product`, and the manual table ignored the KPI. State must never depend on a
 * field the product cannot set (ADR-0048) — so the mode moved to
 * {@link classifyTtiStateReason}, which decides only what a not-measurable TTI is
 * TOLD to do. That is also what PB-2924's plan authorised: one new state clause.
 *
 * THE ONE NEW CLAUSE is in `measured` — a hand-entered actual counts as measurement
 * alongside a governed reading. Everything after it is unchanged, so with no
 * recorded actual this function behaves exactly as it did before PB-2924, which is
 * why the KPI-measured contracts (PB-96 / PB-280 / PB-281 / PB-288) hold without a
 * single fixture edit.
 *
 * FAIL-SAFE, and this is the property to protect: a recorded actual can only LIFT
 * `missing` — it can never beat `stale`. A TTI whose governed source is stale still
 * reads `stale` even with a figure typed against it, so a response can never claim
 * `active` beside `measurementStatus: "stale"`. Every absent or unknown input still
 * pushes toward `missing`.
 *
 * Known gap, unchanged by this design: nothing makes a manually tracked TTI go
 * `stale` when its owner stops updating. Staleness comes from `measurementStatus`,
 * and a rule for hand-entered figures would need its own decision about what counts
 * as late. It was not asked for.
 */
export function classifyTtiState(inputs) {
    const measurement = inputs.measurementStatus ?? "not_connected";
    // ADR-0039 is folded in upstream: a non-lifecycle-active KPI arrives with
    // `measurementStatus: null`, which reads here as `not_connected`.
    const governedMeasured = inputs.hasKpi && measurement !== "not_connected";
    const measured = governedMeasured || inputs.hasRecordedActual;
    if (!measured)
        return "missing";
    if (!inputs.hasTargetValue)
        return "inactive";
    if (measurement === "stale" || measurement === "unavailable")
        return "stale";
    return "active";
}
/**
 * Why a TTI is not measurable, or `null` when it is (PB-283) — the companion to
 * {@link classifyTtiState} over the SAME {@link TtiStateInputs}. Separate
 * function rather than a widened return type: `classifyTtiState` is a shared
 * export with three call sites, and changing its shape would be a breaking
 * `packages/types` change for no gain.
 *
 * This is the ONLY place `trackingMode` is read (ADR-0048). The mode does not change
 * whether a TTI is measurable — it changes who is being asked to act, and therefore which copy
 * the badge shows. That is the whole of PB-2924's read-side change.
 *
 * A reason is non-null **iff** the state is `missing` or `inactive`, in both modes,
 * because both functions branch on the same `measured` and `hasTargetValue`. A table
 * test over every input combination pins that, so reordering a branch in either
 * function fails loudly.
 */
export function classifyTtiStateReason(inputs) {
    const measurement = inputs.measurementStatus ?? "not_connected";
    const governedMeasured = inputs.hasKpi && measurement !== "not_connected";
    const measured = governedMeasured || inputs.hasRecordedActual;
    // Measurable AND targeted: `active` or `stale`, neither of which needs an
    // instruction — `measurementStatus` already states those facts.
    if (measured && inputs.hasTargetValue)
        return null;
    // MANUAL — the column's default, so every TTI today. The owner is the reporter, so
    // neither "pick a KPI" nor "wait for a data product" is a true instruction:
    // whichever blocker applies, the next action is theirs.
    if (inputs.trackingMode === "manual") {
        return measured ? "target_value_not_set" : "awaiting_manual_update";
    }
    // DATA_PRODUCT — the pre-PB-2924 precedence, verbatim: no measure chosen first, so
    // a TTI with none is never told to wait for data nobody requested.
    if (!inputs.hasKpi)
        return "kpi_not_linked";
    if (measurement === "not_connected")
        return "data_product_required";
    return "target_value_not_set";
}
/**
 * Whether a TTI's target value is set — a non-blank string. The single home of
 * the "has a target" rule that feeds `classifyTtiState`'s `hasTargetValue`, so
 * the FE (`deriveTtiState`), the backend read mapper, and the snapshot resolver
 * cannot drift on what counts as a set target (e.g. whitespace stays unset).
 */
export function hasNonBlankTargetValue(value) {
    return (value ?? "").trim() !== "";
}
/**
 * Narrow a raw DB/source string to a known `MeasurementStatus`, or `null` if it
 * is unrecognised. The backend reads the column as a plain `String` (the
 * vocabulary is a DB CHECK, not a Prisma enum), so an unexpected value — e.g. a
 * status the KPI domain adds that Hoshin has not caught up to — must NOT be cast
 * through unchecked: it folds to `null` here → `not_connected` in
 * `classifyTtiState` → reads `missing`, never mis-classified as measured
 * (fail-safe, PB-96).
 */
export function asMeasurementStatus(value) {
    return value != null &&
        MEASUREMENT_STATUSES.includes(value)
        ? value
        : null;
}
/** Server-stamped provenance for a Kaizen created directly from the Prioritize
 * parking tray. It stays eligible for that tray without inventing scores. */
export const HOSHIN_MATRIX_KAIZEN_SOURCE = "manual_matrix_kaizen";
//# sourceMappingURL=hoshin.js.map